import { useState, useEffect } from 'react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'
import {Lock, CheckCircle, AlertTriangle, ArrowLeft} from 'lucide-react'

interface TokenPayload {
  usuario: string
  exp: number // timestamp em ms
}

function decodeToken(token: string): TokenPayload | null {
  try {
    const decoded = atob(token)
    const parsed = JSON.parse(decoded) as TokenPayload
    if (!parsed.usuario || !parsed.exp) return null
    return parsed
  } catch {
    return null
  }
}

export default function RedefinirSenha() {
  const [loading, setLoading] = useState(true)
  const [tokenValido, setTokenValido] = useState(false)
  const [tokenExpirado, setTokenExpirado] = useState(false)
  const [consultoraId, setConsultoraId] = useState<string | null>(null)
  const [nomeConsultora, setNomeConsultora] = useState('')
  const [usuario, setUsuario] = useState('')
  const [novaSenha, setNovaSenha] = useState('')
  const [confirmarSenha, setConfirmarSenha] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [sucesso, setSucesso] = useState(false)

  useEffect(() => {
    validarToken()
  }, [])

  const validarToken = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams(window.location.search)
      const token = params.get('token')
      if (!token) {
        setTokenValido(false)
        setLoading(false)
        return
      }

      const payload = decodeToken(token)
      if (!payload) {
        setTokenValido(false)
        setLoading(false)
        return
      }

      // Verificar expiração
      if (Date.now() > payload.exp) {
        setTokenExpirado(true)
        setTokenValido(false)
        setLoading(false)
        return
      }

      // Verificar se consultora existe e se o token ainda é o atual
      const resultado = await lumi.entities.consultoras.list({
        filter: { usuario: payload.usuario },
      })
      const consultora = (resultado.list || []).find((c: any) => c.usuario === payload.usuario)

      if (!consultora) {
        setTokenValido(false)
        setLoading(false)
        return
      }

      // Se a consultora já usou um token de reset, ele é armazenado em resetTokenAtivo
      // Se resetTokenAtivo estiver vazio ou for diferente, o link é inválido (single-use)
      if (consultora.resetTokenAtivo && consultora.resetTokenAtivo !== token) {
        setTokenValido(false)
        setTokenExpirado(true)
        setLoading(false)
        return
      }

      setConsultoraId(consultora._id)
      setNomeConsultora(consultora.nome || '')
      setUsuario(consultora.usuario)
      setTokenValido(true)
    } catch (err) {
      console.error('Erro ao validar token:', err)
      setTokenValido(false)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!consultoraId) return

    if (novaSenha.length < 4) {
      toast.error('A nova senha deve ter pelo menos 4 caracteres')
      return
    }
    if (novaSenha !== confirmarSenha) {
      toast.error('As senhas não conferem')
      return
    }

    setSalvando(true)
    try {
      await lumi.entities.consultoras.update(consultoraId, {
        senha: novaSenha.trim(),
        resetTokenAtivo: '', // invalida o token após uso
        updatedAt: new Date().toISOString(),
      })
      setSucesso(true)
      toast.success('Senha redefinida com sucesso!')
      setTimeout(() => {
        window.location.href = '/admin'
      }, 2500)
    } catch (err) {
      console.error('Erro ao redefinir senha:', err)
      toast.error('Erro ao redefinir senha. Tente novamente.')
    } finally {
      setSalvando(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-bege-fundo flex items-center justify-center">
        <div className="text-texto-suave">Validando link...</div>
      </div>
    )
  }

  // Token inválido ou expirado
  if (!tokenValido) {
    return (
      <div className="min-h-screen bg-bege-fundo flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-lg">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-cormorant font-semibold text-texto-escuro text-center mb-3">
            {tokenExpirado ? 'Link expirado' : 'Link inválido'}
          </h1>
          <p className="text-sm text-texto-suave text-center mb-6 leading-relaxed">
            {tokenExpirado
              ? 'Este link de redefinição de senha já expirou ou foi utilizado. Solicite um novo link na tela de login.'
              : 'O link de redefinição de senha é inválido. Verifique se copiou o endereço corretamente ou solicite um novo link.'}
          </p>
          <a
            href="/admin"
            className="w-full flex items-center justify-center gap-2 bg-verde-escuro text-white py-3 rounded-full font-medium hover:bg-opacity-90 transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao login
          </a>
        </div>
      </div>
    )
  }

  // Sucesso
  if (sucesso) {
    return (
      <div className="min-h-screen bg-bege-fundo flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-lg text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-cormorant font-semibold text-texto-escuro mb-3">
            Senha redefinida!
          </h1>
          <p className="text-sm text-texto-suave mb-4">
            Sua senha foi atualizada com sucesso. Redirecionando para o login...
          </p>
          <div className="w-8 h-8 border-4 border-verde-escuro border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    )
  }

  // Formulário de nova senha
  return (
    <div className="min-h-screen bg-bege-fundo flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-lg">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-verde-muito-claro rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-verde-escuro" />
          </div>
          <h1 className="text-2xl font-cormorant font-semibold text-texto-escuro">
            Criar nova senha
          </h1>
          <p className="text-sm text-texto-suave mt-2">
            Olá, <strong>{nomeConsultora.split(' ')[0]}</strong>! Escolha uma nova senha para sua conta.
          </p>
          <p className="text-xs text-texto-suave mt-1">
            Usuário: <span className="font-medium">@{usuario}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm text-texto-suave mb-2 font-medium">Nova senha *</label>
            <input
              type="password"
              value={novaSenha}
              onChange={e => setNovaSenha(e.target.value)}
              className="w-full px-4 py-3 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
              placeholder="Mínimo 4 caracteres"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm text-texto-suave mb-2 font-medium">Confirmar nova senha *</label>
            <input
              type="password"
              value={confirmarSenha}
              onChange={e => setConfirmarSenha(e.target.value)}
              className="w-full px-4 py-3 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
              placeholder="Digite a senha novamente"
            />
          </div>
          <button
            type="submit"
            disabled={salvando}
            className="w-full flex items-center justify-center gap-2 bg-verde-escuro text-white py-3 rounded-full font-medium hover:bg-opacity-90 transition-all disabled:opacity-60"
          >
            {salvando ? 'Salvando...' : 'Redefinir senha'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <a href="/admin" className="text-sm text-verde-escuro hover:underline">
            Voltar ao login
          </a>
        </div>
      </div>
    </div>
  )
}
